import React, { useState } from 'react';
import { Phone, Mail, MapPin, X, Send, PhoneCall } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';

const ContactsPage = ({ client }) => {
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showCallRequestModal, setShowCallRequestModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [callReason, setCallReason] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [sending, setSending] = useState(false);

  const contact = client.contact || {
    advisorName: 'Service Client Fortuneo',
    advisorPhone: '01 46 09 49 49',
    advisorEmail: 'contact@fortuneo.fr',
    agencyAddress: '110 Esplanade du Général de Gaulle, 92931 Paris La Défense'
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    try {
      setSending(true);
      await api.sendMessage(client.id, {
        type: 'message',
        subject: messageSubject,
        content: messageContent
      });
      toast.success('Message envoyé avec succès');
      setShowMessageModal(false);
      setMessageSubject('');
      setMessageContent('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleRequestCall = async () => {
    if (!callReason.trim()) {
      toast.error('Veuillez indiquer la raison de l\'appel');
      return;
    }

    try {
      setSending(true);
      await api.sendMessage(client.id, {
        type: 'call_request',
        subject: 'Demande de rappel',
        content: `Raison: ${callReason}\nCréneau préféré: ${preferredTime || 'Non spécifié'}\nTéléphone: ${client.phone}`
      });
      toast.success('Demande de rappel envoyée');
      setShowCallRequestModal(false);
      setCallReason('');
      setPreferredTime('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Conseiller en crédit immobilier</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 p-3 rounded-lg">
              <Phone className="text-green-600" size={20} />
            </div>
            <div>
              <p className="font-medium">{contact.advisorName}</p>
              <p className="text-sm text-gray-600 mt-1">Conseiller immobilier</p>
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center space-x-3">
              <Phone className="text-gray-400" size={18} />
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="font-medium">{contact.advisorPhone}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Mail className="text-gray-400" size={18} />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{contact.advisorEmail}</p>
              </div>
            </div>

            {contact.agencyAddress && (
              <div className="flex items-start space-x-3">
                <MapPin className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-sm text-gray-500">Adresse</p>
                  <p className="font-medium">{contact.agencyAddress}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Besoin d&apos;aide ?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Button 
              className="w-full" 
              style={{ backgroundColor: '#82C43C' }}
              onClick={() => setShowCallRequestModal(true)}
              data-testid="request-call-btn"
            >
              <PhoneCall size={18} className="mr-2" />
              Demander un rappel
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowMessageModal(true)}
              data-testid="send-message-btn"
            >
              <Mail size={18} className="mr-2" />
              Envoyer un message
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Envoyer un message</h2>
              <Button onClick={() => setShowMessageModal(false)} variant="ghost" size="sm">
                <X size={20} />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Sujet (optionnel)</Label>
                <Input
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                  placeholder="Ex: Question sur mon dossier"
                  className="mt-1"
                  data-testid="message-subject-input"
                />
              </div>
              <div>
                <Label>Votre message *</Label>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Écrivez votre message ici..."
                  className="w-full border rounded-lg p-3 mt-1 min-h-[150px] resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="message-content-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleSendMessage} 
                  disabled={sending || !messageContent.trim()}
                  className="flex-1"
                  style={{ backgroundColor: '#82C43C' }}
                  data-testid="submit-message-btn"
                >
                  <Send size={16} className="mr-2" />
                  {sending ? 'Envoi...' : 'Envoyer'}
                </Button>
                <Button variant="outline" onClick={() => setShowMessageModal(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Request Modal */}
      {showCallRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Demander un rappel</h2>
              <Button onClick={() => setShowCallRequestModal(false)} variant="ghost" size="sm">
                <X size={20} />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  📞 Votre numéro de téléphone: <strong>{client.phone}</strong>
                </p>
              </div>
              <div>
                <Label>Raison de l&apos;appel *</Label>
                <textarea
                  value={callReason}
                  onChange={(e) => setCallReason(e.target.value)}
                  placeholder="Ex: J'ai des questions sur mon offre de prêt..."
                  className="w-full border rounded-lg p-3 mt-1 min-h-[100px] resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="call-reason-input"
                />
              </div>
              <div>
                <Label>Créneau préféré (optionnel)</Label>
                <Input
                  value={preferredTime}
                  onChange={(e) => setPreferredTime(e.target.value)}
                  placeholder="Ex: Mardi matin, après 14h..."
                  className="mt-1"
                  data-testid="preferred-time-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleRequestCall} 
                  disabled={sending || !callReason.trim()}
                  className="flex-1"
                  style={{ backgroundColor: '#82C43C' }}
                  data-testid="submit-call-request-btn"
                >
                  <PhoneCall size={16} className="mr-2" />
                  {sending ? 'Envoi...' : 'Demander un rappel'}
                </Button>
                <Button variant="outline" onClick={() => setShowCallRequestModal(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
